/**
 * These values only concern the verify flow.
 * On Dashboard, you likely want to use models/verification-templates instead.
 */
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import DeviceHandoffOption from 'lib/interfaces/DeviceHandoffOption';
import { ImageRemoteComponent } from 'lib/interfaces/ui-components';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

export interface VerificationCheckConfig {
  check: CheckName;
  'on-failed': VerificationStatus.Failed | VerificationStatus.RequiresRetry | null;
}

export enum Disclaimer {
  Default = 'default',
  None = 'none',
}

export interface VerificationConfigGovernmentId {
  'enabled-id-classes-by-country'?: GovernmentIdEnabledIdClassByCountries;
  'enabled-skip-unknown'?: boolean;
  'enabled-capture-options-desktop'?: Array<
    | GovernmentIdCaptureOption.WebCamera
    | GovernmentIdCaptureOption.Upload
    | GovernmentIdCaptureOption.DeviceHandoff
  >;
  'enabled-capture-options-mobile'?: Array<
    | GovernmentIdCaptureOption.WebCamera
    | GovernmentIdCaptureOption.MobileCamera
    | GovernmentIdCaptureOption.Upload
    | GovernmentIdCaptureOption.DeviceHandoff
  >;
  'verification-checks'?: VerificationCheckConfig[];
  disclaimer?: Disclaimer;
  'device-handoff-options'?: DeviceHandoffOption[];
  'camera-error-device-handoff-options'?: DeviceHandoffOption[];
}

export type GovernmentIdEnabledIdClassByCountries = Record<
  CountryCodeLower,
  Array<GovernmentIdEnabledIdClassByCountryEntry>
>;

export enum RequiredDevice {
  None = 'none',
  Mobile = 'mobile',
}

export enum GovernmentIdSide {
  Front = 'front',
  Back = 'back',
  BarcodePdf417 = 'barcode_pdf417',
  FrontOrBack = 'front_or_back',
  PassportSignature = 'passport_signature',
}

export type GovernmentIdSideVideo = 'front_and_back';

export interface GovernmentIdEnabledIdClassByCountryEntry {
  class: GovernmentIdClass;
  'requires-device': RequiredDevice;
  'requires-sides': GovernmentIdSide[];
  'capture-page-configs'?: GovIdCapturePage[];
  icon?: GovIdIconConfig;
}

interface GovIdIconConfig {
  'custom-icon'?: ImageRemoteComponent;
  'icon-fallback': GovIdIcon;
}

export enum GovIdIcon {
  Flag = 'flag',
  World = 'world',
  House = 'house',
  Card = 'card',
}

export interface GovIdCapturePage {
  'auto-capture-config': AutoCaptureConfig;
  'manual-capture-config': ManualCaptureConfig;
  overlay: GovernmentIdOverlay;
  side: GovernmentIdSide;
}

/**
 * From Dynamic Government ID. Allows configuring custom overlay elements.
 *
 * See Inquiry::Step::DynamicFlow::GovernmentId::IdClasses#get_default_overlay_for_side
 * for defaults.
 */
export interface GovernmentIdOverlay {
  'overlay-fallback': ClientSideOverlay;
  overlay: ImageRemoteComponent;
  style: unknown;
}

export enum ClientSideOverlay {
  Barcode = 'barcode',
  Passport = 'passport',
  IdFront = 'id_front',
  IdFrontRightPortrait = 'id_front_right_portrait',
  IdBack = 'id_back',
  CornersOnly = 'corners_only',
  Empty = 'empty',
}

export interface AutoCaptureConfig {
  'rule-sets': AutoCaptureRuleSet[];
}

export interface AutoCaptureRuleSet {
  rules: AutoCaptureRule[];
}

export enum AutoCaptureRuleType {
  IdFront = 'id_front',
  IdFrontOrBack = 'id_front_or_back',
  BarcodePdf417 = 'barcode_pdf417',
  PassportMrz = 'passport_mrz',
}

interface AutoCaptureRule {
  type: AutoCaptureRuleType;
  'is-required': boolean;
  options: unknown;
}

interface ManualCaptureConfig {
  'delay-ms': number;
  'is-enabled': boolean;
}

export interface VerificationConfigEmailAddress {
  'num-attempts': number;
}

export interface VerificationConfigPhoneNumber {
  'require-info-match'?: PhoneNumberInfoMatch;
  'enabled-signals'?: PhoneNumberSignal[];
  'num-attempts': number;
}

export interface VerificationConfigSelfie {
  'require-selfie-match'?: SelfieMatch;
  'enabled-capture-options-desktop'?: SelfieCaptureOption[];
  'enabled-capture-options-mobile'?: SelfieCaptureOption[];
  'device-handoff-options'?: DeviceHandoffOption[];
  'require-strict-selfie-capture'?: boolean;
}

export interface VerificationConfigDocument {
  'match-patterns'?: DocumentMatchPattern[];
}

export interface VerificationConfigDatabase {
  'accepted-match-results': {
    name?: AcceptedMatchResult;
    birthdate?: AcceptedMatchResult;
    address?: AcceptedMatchResult;
    'social-security-number'?: AcceptedMatchResult;
    'phone-number'?: AcceptedMatchResult;
    'email-address'?: AcceptedMatchResult;
  };
  'input-fields'?: InputField[];
  'address-autocomplete-method'?: AddressAutocompleteMethod;
}

export interface VerificationConfigDatabaseEcbsv {
  'input-fields'?: InputField[];
  'authorizing-consent-reason'?: AuthorizingConsentReason;
  'authorizing-consent-reason-other-text'?: string;
}

export enum InputField {
  Name = 'name',
  Birthdate = 'birthdate',
  Address = 'address',
  IdentificationNumberLast4 = 'identification_number_last_4',
  IdentificationNumberFull9 = 'identification_number_full_9',
  PhoneNumber = 'phone_number',
  EmailAddress = 'email_address',
}

export type VerificationConfig =
  | VerificationConfigDocument
  | VerificationConfigDatabase
  | VerificationConfigDatabaseEcbsv
  | VerificationConfigGovernmentId
  | VerificationConfigPhoneNumber
  | VerificationConfigEmailAddress
  | VerificationConfigSelfie;

export enum AcceptedMatchResult {
  Full = 'full',
  Partial = 'partial',
  None = 'none',
}

export type DocumentMatchPattern = {
  name: string;
  token: string;
  formats: string[];
  threshold?: number;
};

enum AuthorizingConsentReason {
  ApplyForMortgage = 'apply_for_mortgage',
  OpenBankAccount = 'open_bank_account',
  ApplyForCreditCard = 'apply_for_credit_card',
  ApplyForLoan = 'apply_for_loan',
  OpenRetirementAccount = 'open_retirement_account',
  ApplyForJob = 'apply_for_job',
  MeetLicensingRequirement = 'meet_licensing_requirement',
  Other = 'other',
}

export enum GovernmentIdAttribute {
  NameFirst = 'name_first',
  NameLast = 'name_last',
  Birthdate = 'birthdate',
  IdentificationNumber = 'identification_number',
  AddressStreet1 = 'address_street_1',
  AddressStreet2 = 'address_street_2',
  AddressCity = 'address_city',
  AddressSubdivision = 'address_subdivision',
  AddressPostalCode = 'address_postal_code',
}

export enum GovernmentIdCaptureOption {
  WebCamera = 'web_camera',
  MobileCamera = 'mobile_camera',
  Upload = 'upload',
  DeviceHandoff = 'device_handoff',
}

export enum PhoneNumberInfoMatch {
  None = 'none',
  Name = 'name',
  NameAndBirthdate = 'name_and_birthdate',
  NameAndSsn = 'name_and_ssn',
}

export enum PhoneNumberSignal {
  Telesign = 'telesign',
}

export enum SelfieMatch {
  ProfileAndCenter = 'profile_and_center',
  OnlyCenter = 'only_center',
  ConfigurablePoses = 'configurable_poses',
  Reflection = 'reflection',
}

export enum SelfieCaptureOption {
  WebCamera = 'web_camera',
  Manual = 'manual',
  Stillfie = 'stillfie',
  DeviceHandoff = 'device_handoff',
}

export type SelfieCaptureFileType = 'image' | 'video';

export enum SelfieVideoCaptureMethod {
  Stream = 'stream',
  Upload = 'upload',
}

export enum AddressAutocompleteMethod {
  None = 'none',
  GoogleMaps = 'google_maps',
}

// NOTE: this mirrors the CountryCode constant, and is only used in configs due to key inflection
export type CountryCodeLower =
  | 'ad'
  | 'ae'
  | 'af'
  | 'ag'
  | 'ai'
  | 'al'
  | 'am'
  | 'ao'
  | 'aq'
  | 'ar'
  | 'as'
  | 'at'
  | 'au'
  | 'aw'
  | 'ax'
  | 'az'
  | 'ba'
  | 'bb'
  | 'bd'
  | 'be'
  | 'bf'
  | 'bg'
  | 'bh'
  | 'bi'
  | 'bj'
  | 'bl'
  | 'bm'
  | 'bn'
  | 'bo'
  | 'bq'
  | 'br'
  | 'bs'
  | 'bt'
  | 'bv'
  | 'bw'
  | 'by'
  | 'bz'
  | 'ca'
  | 'cc'
  | 'cd'
  | 'cf'
  | 'cg'
  | 'ch'
  | 'ci'
  | 'ck'
  | 'cl'
  | 'cm'
  | 'cn'
  | 'co'
  | 'cr'
  | 'cu'
  | 'cv'
  | 'cw'
  | 'cx'
  | 'cy'
  | 'cz'
  | 'de'
  | 'dj'
  | 'dk'
  | 'dm'
  | 'do'
  | 'dz'
  | 'ec'
  | 'ee'
  | 'eg'
  | 'eh'
  | 'er'
  | 'es'
  | 'et'
  | 'fi'
  | 'fj'
  | 'fk'
  | 'fm'
  | 'fo'
  | 'fr'
  | 'ga'
  | 'gb'
  | 'gd'
  | 'ge'
  | 'gf'
  | 'gg'
  | 'gh'
  | 'gi'
  | 'gl'
  | 'gm'
  | 'gn'
  | 'gp'
  | 'gq'
  | 'gr'
  | 'gs'
  | 'gt'
  | 'gu'
  | 'gw'
  | 'gy'
  | 'hk'
  | 'hm'
  | 'hn'
  | 'hr'
  | 'ht'
  | 'hu'
  | 'id'
  | 'ie'
  | 'il'
  | 'im'
  | 'in'
  | 'io'
  | 'iq'
  | 'ir'
  | 'is'
  | 'it'
  | 'je'
  | 'jm'
  | 'jo'
  | 'jp'
  | 'ke'
  | 'kg'
  | 'kh'
  | 'ki'
  | 'km'
  | 'kn'
  | 'kp'
  | 'kr'
  | 'kw'
  | 'ky'
  | 'kz'
  | 'la'
  | 'lb'
  | 'lc'
  | 'li'
  | 'lk'
  | 'lr'
  | 'ls'
  | 'lt'
  | 'lu'
  | 'lv'
  | 'ly'
  | 'ma'
  | 'mc'
  | 'md'
  | 'me'
  | 'mf'
  | 'mg'
  | 'mh'
  | 'mk'
  | 'ml'
  | 'mm'
  | 'mn'
  | 'mo'
  | 'mp'
  | 'mq'
  | 'mr'
  | 'ms'
  | 'mt'
  | 'mu'
  | 'mv'
  | 'mw'
  | 'mx'
  | 'my'
  | 'mz'
  | 'na'
  | 'nc'
  | 'ne'
  | 'nf'
  | 'ng'
  | 'ni'
  | 'nl'
  | 'no'
  | 'np'
  | 'nr'
  | 'nu'
  | 'nz'
  | 'om'
  | 'pa'
  | 'pe'
  | 'pf'
  | 'pg'
  | 'ph'
  | 'pk'
  | 'pl'
  | 'pm'
  | 'pn'
  | 'pr'
  | 'ps'
  | 'pt'
  | 'pw'
  | 'py'
  | 'qa'
  | 're'
  | 'ro'
  | 'rs'
  | 'ru'
  | 'rw'
  | 'sa'
  | 'sb'
  | 'sc'
  | 'sd'
  | 'se'
  | 'sg'
  | 'sh'
  | 'si'
  | 'sj'
  | 'sk'
  | 'sl'
  | 'sm'
  | 'sn'
  | 'so'
  | 'sr'
  | 'ss'
  | 'st'
  | 'sv'
  | 'sx'
  | 'sy'
  | 'sz'
  | 'tc'
  | 'td'
  | 'tf'
  | 'tg'
  | 'th'
  | 'tj'
  | 'tk'
  | 'tl'
  | 'tm'
  | 'tn'
  | 'to'
  | 'tr'
  | 'tt'
  | 'tv'
  | 'tw'
  | 'tz'
  | 'ua'
  | 'ug'
  | 'um'
  | 'us'
  | 'uy'
  | 'uz'
  | 'va'
  | 'vc'
  | 've'
  | 'vg'
  | 'vi'
  | 'vn'
  | 'vu'
  | 'wf'
  | 'ws'
  | 'xk'
  | 'ye'
  | 'yt'
  | 'za'
  | 'zm'
  | 'zw';
